/**
 * Toast 컴포넌트
 * 상단에 나타나는 알림 토스트 - 심플 네이티브 스타일
 */

import { useEffect, useState } from "react";
import { useToast, type ToastType } from "@/contexts/ToastContext";

/** 토스트 타입별 스타일 */
const TOAST_STYLES: Record<ToastType, { bg: string; iconBg: string; icon: string }> = {
  success: { bg: "bg-slate-800", iconBg: "bg-emerald-500", icon: "✓" },
  error: { bg: "bg-red-600", iconBg: "bg-white/20", icon: "✕" },
  info: { bg: "bg-slate-800", iconBg: "bg-white/20", icon: "i" },
  warning: { bg: "bg-amber-600", iconBg: "bg-white/20", icon: "!" },
};

/** 개별 토스트 아이템 */
function ToastItem({
  id,
  message,
  type,
  onRemove,
}: {
  id: string;
  message: string;
  type: ToastType;
  onRemove: (id: string) => void;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const style = TOAST_STYLES[type];

  // 등장 애니메이션
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  // 2.5초 후 제거
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove(id), 200);
    }, 2500);
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  return (
    <div
      className={`
        ${style.bg} flex items-center gap-2.5 rounded-lg px-4 py-3 shadow-lg
        transition-all duration-200 ease-out
        ${isVisible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"}
      `}
      role="alert"
    >
      {/* 아이콘 */}
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white ${style.iconBg}`}
      >
        {style.icon}
      </span>

      {/* 메시지 */}
      <p className="flex-1 text-sm font-medium text-white">{message}</p>
    </div>
  );
}

/** 토스트 컨테이너 */
export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex flex-col items-center gap-2 px-4"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            id={toast.id}
            message={toast.message}
            type={toast.type}
            onRemove={removeToast}
          />
        </div>
      ))}
    </div>
  );
}
